import type { ReactNode } from 'react';
import { Stack, Text } from '@mantine/core';

/**
 * A setting as it is, written to be read.
 *
 * The value and where it came from are one sentence rather than a field and a badge beside it:
 * "Double opt in, inherited from Northern News Group" is the answer somebody came to this screen
 * for, and it is an answer a person can act on without first working out what a grey badge next to
 * a filled in box is telling them. A disabled input says the same thing in a way that looks broken
 * and reads as a form somebody is not allowed to use.
 *
 * A description list, because that is what this is: a list of names and what each one holds.
 */
export function ReadList({ children }: { children: ReactNode }) {
  return (
    <Stack className="settings-read-list" component="dl" gap={0} style={{ margin: 0 }}>
      {children}
    </Stack>
  );
}

export function ReadRow({
  label,
  value,
  source,
  note,
  swatch,
}: {
  label: string;
  /** What it is now. Never a placeholder that could be mistaken for a value. */
  value: ReactNode;
  /** Where it came from, as the end of the sentence: "inherited from Northern News Group". */
  source?: string | undefined;
  /** What the value means for a reader, when that is not obvious from the value itself. */
  note?: ReactNode | undefined;
  /** A colour, shown as well as named. Decoration: the code beside it is the value. */
  swatch?: string | undefined;
}) {
  return (
    <div>
      <Text component="dt" size="sm" c="dimmed">
        {label}
      </Text>
      <Text component="dd" style={{ margin: 0 }}>
        {swatch !== undefined && (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: '0.9em',
              height: '0.9em',
              marginRight: 6,
              borderRadius: 3,
              verticalAlign: 'baseline',
              background: swatch,
              border: '1px solid var(--mantine-color-default-border)',
            }}
          />
        )}
        {value}
        {source !== undefined && (
          <Text component="span" c="dimmed">
            , {source}
          </Text>
        )}
        {note !== undefined && (
          <Text size="sm" c="dimmed" mt={2}>
            {note}
          </Text>
        )}
      </Text>
    </div>
  );
}

/**
 * The end of the sentence a value is written in.
 *
 * "Set here" and "inherited from" are the same two words these screens have always used for the
 * distinction, kept because the distinction is the thing people learn about this product, not the
 * wording.
 */
export function sourceWords(overridden: boolean, from: string): string {
  return overridden ? 'set here' : `inherited from ${from}`;
}
